import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import {
    Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Gaming popup (Menu -> Gaming, and the /gaming/roosbreaker route).
// Rocket Orbit Jumper is a static browser game served from
// public/games/rocket-orbit-jumper/ (copied from the rocket-jump project;
// re-copy it there after game updates, including preview.jpg if the look changes).

const BRAND = '#166fe5';
const DIALOG_Z_INDEX = 10000000000; // same convention as the other Menu dialogs
const GAME_URL = '/games/rocket-orbit-jumper/index.html';
const GAME_PREVIEW = '/games/rocket-orbit-jumper/preview.jpg';

export const GamingDialog = ({ open, onClose }) => {
    const mode = useSelector(state => state.themeswitch) === 'dark' ? 'dark' : 'light';
    const dark = mode === 'dark';

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode,
            primary: { main: BRAND },
            background: dark ? { paper: '#0D1F2D', default: '#0D1F2D' } : { paper: '#fff', default: '#fff' }
        }
    }), [mode, dark]);

    const colors = {
        text: dark ? '#fff' : '#000',
        muted: dark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)',
        border: dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
        // brand blue lightened on the dark background for readable text contrast
        accent: dark ? '#7fb2ff' : BRAND,
        accentSoft: dark ? 'rgba(22,111,229,0.22)' : 'rgba(22,111,229,0.10)'
    };

    const chipSx = {
        fontWeight: 600,
        color: colors.accent,
        bgcolor: colors.accentSoft,
        border: 'none'
    };

    return (
        <ThemeProvider theme={muiTheme}>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="xs"
                aria-labelledby="gaming-dialog-title"
                aria-describedby="gaming-dialog-description"
                style={{ zIndex: DIALOG_Z_INDEX }}
                PaperProps={{ sx: { bgcolor: dark ? '#0D1F2D' : '#fff', backgroundImage: 'none', color: colors.text, borderRadius: 3, m: 2, width: 'calc(100% - 32px)' } }}
            >
                <DialogTitle id="gaming-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 7, color: colors.text, fontWeight: 700 }}>
                    <SportsEsportsIcon aria-hidden="true" sx={{ color: colors.accent }} />
                    Gaming
                </DialogTitle>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12, color: colors.muted }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogContent sx={{ pt: 0, pb: 3 }}>
                    <Box
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: colors.border,
                            bgcolor: '#070b1f', // matches the game's night sky if the image is slow or missing
                            boxShadow: dark ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(13,31,45,0.18)'
                        }}
                    >
                        <Box
                            component="img"
                            src={GAME_PREVIEW}
                            alt="Rocket Orbit Jumper gameplay: a rocket orbiting colourful planets among the stars"
                            sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }}
                        />
                    </Box>

                    <Typography component="h3" sx={{ mt: 2.5, fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.25, color: colors.text }}>
                        Rocket Orbit Jumper
                    </Typography>
                    <Typography id="gaming-dialog-description" sx={{ mt: 0.75, fontSize: '0.95rem', lineHeight: 1.5, color: colors.muted }}>
                        Orbit the planets, time your launch and jump from world to world. How far can you fly?
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }} aria-label="Game details">
                        <Chip size="small" label="Free" sx={chipSx} />
                        <Chip size="small" label="Browser game" sx={chipSx} />
                        <Chip size="small" label="Mobile & desktop" sx={chipSx} />
                    </Stack>

                    <Button
                        component="a"
                        href={GAME_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                        variant="contained"
                        size="large"
                        fullWidth
                        disableElevation
                        startIcon={<PlayArrowRoundedIcon aria-hidden="true" />}
                        endIcon={<OpenInNewIcon aria-hidden="true" sx={{ fontSize: '1rem !important', opacity: 0.85 }} />}
                        aria-label="Play Rocket Orbit Jumper (opens in a new tab)"
                        sx={{
                            mt: 3,
                            py: 1.25,
                            borderRadius: 20,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            bgcolor: BRAND,
                            color: '#fff',
                            '&:hover': { bgcolor: '#125bbd', color: '#fff' }
                        }}
                    >
                        Play now
                    </Button>
                    <Typography sx={{ mt: 1.25, textAlign: 'center', fontSize: '0.8rem', color: colors.muted }}>
                        Opens in a new tab
                    </Typography>

                    {/* Roos Breaker (Android app) is no longer promoted here. Kept commented so it can be restored.
                        The /gaming/roosbreaker route and the /gaming/privacy-policy and /gaming/terms-of-use pages
                        stay live because the Play Store listing may still link to them.
                    <div class="row">
                        <div class="col">
                            <a href='https://play.google.com/store/apps/details?id=com.onelikegaming.roosbreaker' title='Download Roos Breaker' target={"blank_"} style={{ textDecoration: "none" }}>
                                <div class="row">
                                    <div class="col" style={{ padding: 20, textAlign: "center" }}><img style={{ width: 100 }} src='/logo-roos-dark-rounded.png' alt='Download Roos Breaker' /></div>
                                    <div class="col">
                                        <p style={{ padding: 20, fontSize: 30, textAlign: "center" }}><a style={{ textDecoration: "none" }}>Roos Breaker</a></p>
                                        <p style={{ padding: 20, fontSize: 20, textAlign: "center" }}>Complete Version</p>
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div class="row">
                        <span><a style={{ textDecoration: "none", fontSize: 12 }} href='https://roosbreaker-gaming.onelike.app' target={"blank_"}>FAQ (For Gaming)</a></span>
                    </div>
                    <div class="row">
                        <span><a style={{ textDecoration: "none", fontSize: 12 }} href='/gaming/privacy-policy' target={"blank_"}>Privacy Policy (For Gaming)</a></span>
                    </div>
                    <div class="row">
                        <span><a style={{ textDecoration: "none", fontSize: 12 }} href='/gaming/terms-of-use' target={"blank_"}>Terms & Conditions (For Gaming)</a></span>
                    </div>
                    */}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
};
